import { IntegrationType, Provider } from 'domains/models/account/providerType';
import { IntervalType, PlanId } from 'domains/models/subscription/planId';
import { toQueryString } from '../helper';
import { CalendarViewType } from 'domains/models/event_type/calendarViewType';

export interface HomePathProps {}
export interface LoginPageProps {
  to?: string;
  locale?: string;
  isDefaultStepJoinTeam?: boolean;
  inviteTeamUid?: string | null;
  email?: string | null;
  mobileAppUser?: number;
  redirectIfLoggedIn?: boolean;
}
export interface DashboardEventPageProps {
  view?: 'note' | 'message';
  from?: string;
  userId?: string;
  startedAt?: number;
  endedAt?: number;
}
export interface SignUpPageProps {
  to?: string;
  email?: string;
  provider?: Provider | null;
  inviteTeamUid?: string | null;
  locale?: string;
  skipOnboard?: number;
  mobileAppUser?: number;
  hiddenPasswordProvider?: number;
}
export interface LogoutPageProps {
  to?: string;
  userNotificationToken?: string;
}
export interface PricingPageProps {
  cancelPlanId?: PlanId;
  intervalType?: IntervalType;
}
export interface HostEventTypeEditPageProps {
  eventTypeId: string;
}
export interface HostServiceEditPageProps {
  serviceId: string;
  from?: string;
}
export interface HostServiceNewPageProps {
  from: string;
  isRequired: boolean;
}
export interface HostTaxRateEditPageProps {
  taxRateId: string;
}
export interface HostCancelPolicyEditPageProps {
  cancelPolicyId: string;
  from?: string;
}
export interface HostTermEditPageProps {
  termId: string;
  from?: string;
}
export interface HostCancelPolicyNewPageProps {
  from?: string;
}
export interface HostTermNewPageProps {
  from?: string;
}
export interface AccountBillingPageProps {
  billingId: string;
}
export interface AppCalendarPageProps {
  year?: number | null;
  month?: number;
  display?: number;
  filter?: string;
}

export interface AdminPlanUpdatedPageProps {
  planId: PlanId;
  quantity: number;
  intervalType: IntervalType;
}

export interface CdnApiAssetPathProps {
  locale: string | null;
}

export const enterpriseInquiryPath = () => {
  return contactPath();
  // return 'https://share.hsforms.com/150KFrn8gQMipN7xLuTG-hAcdglk';
};
export const webPath = (path: string, locale?: string | null) => {
  return `${process.env['JICOO_HOST']}${addLocalePath(locale)}${path}`;
};

export const addLocalePath = (locale?: string | null) => {
  return locale && locale !== 'ja' ? `/${locale}` : '';
};

export const apiPath = (path: string, props: any | null = null) => {
  let queryString = '';
  if (props) {
    queryString = toQueryString(props);
    if (queryString) queryString = `?${queryString}`;
  }
  return `${process.env['JICOO_API_HOST']}${path}${queryString}`;
};

export const cdnApiAssetPath = (
  path: string,
  props: CdnApiAssetPathProps | null = null,
) => {
  let queryString = '';
  if (props) {
    queryString = toQueryString(props);
    if (queryString) queryString = `?${queryString}`;
  }
  return `${process.env['JICOO_CDN_API_ASSET_HOST']}${path}${queryString}`;
};

export const launchAppPath = (to?: string, force?: boolean) => {
  return to || force
    ? `/launch_app?${toQueryString({
        to,
        force: force === true ? 1 : undefined,
      })}`
    : '/launch_app';
};
export const openAppPath = (url: string) => {
  return `jicoo://open?url=${encodeURIComponent(url)}`;
};

export const homePath = (anchor: string | null = null) => {
  return '/' + (anchor || '');
};

export enum LoginDefaultStep {
  JoinTeam = 1,
  SelectTeam = 2,
}

export const loginPath = ({
  to,
  locale,
  isDefaultStepJoinTeam,
  inviteTeamUid,
  email,
  mobileAppUser,
  redirectIfLoggedIn,
}: LoginPageProps) => {
  const params = new URLSearchParams();
  params.set('to', to || dashboardPath());
  if (isDefaultStepJoinTeam) {
    params.set('defaultStep', String(LoginDefaultStep.JoinTeam));
  }
  if (inviteTeamUid) {
    params.set('inviteTeam', inviteTeamUid);
  }
  if (email) {
    params.set('email', email);
  }
  if (mobileAppUser === 1) params.set('mobileAppUser', '1');
  if (redirectIfLoggedIn) params.set('redirectIfLoggedIn', '1');
  return `${locale ? `/${locale}` : ''}/login?${params.toString()}`;
};
export const teamLoginPath = () => {
  return `/login/teams`;
};
export const adminLeavePath = () => `/admin/leave`;
export const signUpPath = ({
  to,
  email,
  provider,
  inviteTeamUid,
  locale,
  skipOnboard,
  mobileAppUser,
  hiddenPasswordProvider,
}: SignUpPageProps) => {
  const params = new URLSearchParams();
  params.set('to', to || dashboardPath());
  if (email) params.set('email', email);
  if (inviteTeamUid) params.set('inviteTeam', inviteTeamUid);
  if (provider) params.set('provider', provider);
  if (skipOnboard === 1) params.set('skipOnboard', '1');
  if (mobileAppUser === 1) params.set('mobileAppUser', '1');
  if (hiddenPasswordProvider === 1) params.set('hiddenPasswordProvider', '1');
  return `${locale ? `/${locale}` : ''}/signup?${params.toString()}`;
};
export const logoutPath = ({ to, userNotificationToken }: LogoutPageProps) => {
  const params = new URLSearchParams();
  params.set('to', to || homePath());
  if (userNotificationToken) {
    params.set('userNotificationToken', userNotificationToken);
  }
  return `/logout?${params.toString()}`;
};
export const hostEventTypeListPath = (userId: string | null = null) => {
  if (userId) {
    return `/dashboard/event_types?user=${userId}`;
  }
  return `/dashboard/event_types`;
};
export const hostEventTypeSettingPath = (props?: { from?: string }) => {
  let queryString = '';
  if (props) {
    queryString = toQueryString(props);
    if (queryString) queryString = `?${queryString}`;
  }
  return `/dashboard/event_types/settings${queryString}`;
};
export const hostEventTypeNewPath = (
  projectUid?: string | null,
  from?: string,
) => {
  if (projectUid || from) {
    return `/dashboard/event_types/new?${toQueryString({
      project: projectUid,
      from,
    })}`;
  }
  return `/dashboard/event_types/new`;
};

export const scheduledEventListPath = (props?: {
  userId?: string | null;
  projectId?: string | null;
  tab?: string | number;
  page?: number;
  filters?: {
    createdByPoll?: string;
    eventTypeId?: string;
    eventStatus?: string;
    startedAt?: string;
    endedAt?: string;
    sort?: string;
    order?: string;
  };
}) => {
  if (props) {
    const { userId, tab, filters, projectId, page } = props;
    if (userId || tab || filters || projectId) {
      const params = new URLSearchParams();
      if (userId) {
        params.set('user', userId);
      }
      if (projectId) {
        params.set('project', projectId);
      }
      if (tab) {
        params.set('tab', String(tab));
      }
      if (page) {
        params.set('page', String(page));
      }
      if (filters?.createdByPoll) {
        params.set('createdByPoll', filters.createdByPoll);
      }
      if (filters?.eventTypeId) {
        params.set('eventTypeId', filters.eventTypeId);
      }
      if (filters?.eventStatus) {
        params.set('eventStatus', filters.eventStatus);
      }
      if (filters?.startedAt) {
        params.set('startedAt', filters.startedAt);
      }
      if (filters?.endedAt) {
        params.set('endedAt', filters.endedAt);
      }
      if (filters?.sort) {
        params.set('sort', filters.sort);
      }
      if (filters?.order) {
        params.set('order', filters.order);
      }
      return `/dashboard/schedules?${params.toString()}`;
    }
  }
  return `/dashboard/schedules`;
};

export const formNewPath = (projectUid?: string | null, from?: string) => {
  if (projectUid || from) {
    return `/dashboard/forms/new?${toQueryString({
      project: projectUid,
      from,
    })}`;
  }
  return `/dashboard/forms/new`;
};

export const dashboardFormListPath = (props?: {
  userId?: string | null;
  projectId?: string | null;
  tab?: string | number;
  filters?: {
    eventTypeId?: string;
    sort?: string;
    order?: string;
  };
}) => {
  if (props) {
    const { userId, tab, filters, projectId } = props;
    if (userId || tab || filters || projectId) {
      const params = new URLSearchParams();
      if (userId) {
        params.set('user', userId);
      }
      if (projectId) {
        params.set('project', projectId);
      }
      if (tab) {
        params.set('tab', String(tab));
      }
      if (filters?.eventTypeId) {
        params.set('eventTypeId', filters.eventTypeId);
      }
      if (filters?.sort) {
        params.set('sort', filters.sort);
      }
      if (filters?.order) {
        params.set('order', filters.order);
      }
      return `/dashboard/forms?${params.toString()}`;
    }
  }
  return `/dashboard/forms`;
};

export const hostServiceNewPath = (props?: HostServiceNewPageProps) => {
  const basePath = `/dashboard/services/new`;
  if (!props) return basePath;

  const { from, isRequired } = props;
  if (from) {
    return `${basePath}?from=${encodeURIComponent(from)}&isRequired=${
      isRequired ? 1 : 0
    }`;
  } else {
    return basePath;
  }
};
export const hostServiceEditPath = (props: HostServiceEditPageProps) => {
  const basePath = `/dashboard/services/${props.serviceId}/edit`;
  if (props.from) {
    return `${basePath}?from=${encodeURIComponent(props.from)}`;
  } else {
    return basePath;
  }
};

export const hostWebhookListPath = () => {
  return `/dashboard/webhooks`;
};
export const hostWebhookNewPath = () => {
  return `/dashboard/webhooks/new`;
};
export const hostWebhookEditPath = (uid: string) => {
  return `/dashboard/webhooks/${uid}/edit`;
};

export const hostApiKeyListPath = () => {
  return `/dashboard/api_keys`;
};

export const dashboardTodayPath = (
  userId: string | null = null,
  currentDate?: string | null,
) => {
  const params = new URLSearchParams();

  if (userId) params.set('user', userId);
  if (currentDate) params.set('current', currentDate);

  const q = params.toString();
  if (q) return `/dashboard/today?${q}`;
  return `/dashboard/today`;
};

export enum SearchActiveTab {
  All = 0,
  Contact = 1,
  Event = 2,
  Message = 3,
  Form = 4,
  Note = 5,
  Task = 6,
}

export const dashboardSearchPath = (props: {
  from?: string | null;
  query?: string;
  tab?: SearchActiveTab | null;
  page?: number | null;
}) => {
  const params = new URLSearchParams();
  params.set('from', props.from || dashboardPath());
  if (props.query) params.set('query', props.query);
  if (props.tab) params.set('tab', String(props.tab));
  if (props.page && props.page > 1) params.set('page', String(props.page));
  return `/dashboard/search?${params.toString()}`;
};
export const dashboardEventTypeCalendarPath = (
  from: string,
  props?: { start?: number; end?: number },
) => {
  let queryString = '';
  if (props) {
    queryString = toQueryString(props);
    if (queryString) queryString = `&${queryString}`;
  }
  return `/dashboard/event_types/new?type=oneoff&from=${encodeURIComponent(
    from,
  )}${queryString}`;
};

export const dashboardEventTypePollPath = (
  from: string,
  props?: { start?: number; end?: number },
) => {
  let queryString = '';
  if (props) {
    queryString = toQueryString(props);
    if (queryString) queryString = `&${queryString}`;
  }
  return `/dashboard/event_types/polls/new?from=${encodeURIComponent(
    from,
  )}${queryString}`;
};

export enum EventCreateViewType {
  Calendar = 'calendar',
  Task = 'task',
  Block = 'block',
}

export const dashboardCalendarsPath = (props?: {
  from?: string;
  day?: string;
  month?: string;
  start?: number;
  end?: number;
  eventUser?: string;
  view?: number;
  viewType?: EventCreateViewType;
}) => {
  const params = new URLSearchParams();
  if (props) {
    if (props.from) {
      params.set('from', encodeURIComponent(props.from));
    }
    if (props.day) {
      params.set('day', props.day);
    }
    if (props.month) {
      params.set('month', props.month);
    }
    if (props.start !== undefined) {
      params.set('start', String(props.start));
    }
    if (props.end !== undefined) {
      params.set('end', String(props.end));
    }
    if (props.eventUser) {
      params.set('eventUser', props.eventUser);
    }
    if (props.view !== undefined) {
      params.set('view', String(props.view));
    }
    if (props.viewType) {
      params.set('viewType', props.viewType);
    }
  }
  let url = '/dashboard/calendars';
  const queryString = params.toString();
  if (queryString) url += `?${queryString}`;
  return url;
};

export const dashboardEventEditPath = (
  eventUid: string,
  props?: { from?: string },
) => {
  let queryString = '';
  if (props) {
    queryString = toQueryString(props);
    if (queryString) queryString = `?${queryString}`;
  }
  return `/dashboard/events/${eventUid}/edit${queryString}`;
};

export enum HostEventTypeEditTabIndex {
  Basic = 0,
  Design = 1,
  Form = 2,
  Payment = 3,
  Integration = 4,
  Term = 5,
}

export const hostEventTypeEditPath = (
  eventTypeId: string,
  viewIndex?: number,
  created?: boolean,
  from?: string,
) =>
  viewIndex === undefined && created === undefined && from === undefined
    ? `/dashboard/event_types/${eventTypeId}/edit`
    : `/dashboard/event_types/${eventTypeId}/edit?${toQueryString({
        view: viewIndex,
        created: created === true ? 1 : undefined,
        from,
      })}`;
export const formEditPath = (
  formId: string,
  viewIndex?: number,
  created?: boolean,
  from?: string,
) =>
  viewIndex === undefined && created === undefined && from === undefined
    ? `/dashboard/forms/${formId}/edit`
    : `/dashboard/forms/${formId}/edit?${toQueryString({
        view: viewIndex,
        created: created === true ? 1 : undefined,
        from,
      })}`;
export const guestEventCancellationPath = (eventId: string) =>
  `/events/${eventId}/cancellation`;
export const guestEventReschedulingsPath = (eventId: string) =>
  `/events/${eventId}/rescheduling`;
export const privacyPolicyPath = () => `/privacy_policy`;
export const termsPath = () => `/terms`;
export const aiTermsPath = () => `/terms/ai`;
export const securityPolicyPath = () => `/security`;
export const pricingStartupPath = () => `/pricing/startup`;
export const pricingEducationPath = () => `/pricing/education`;
export const contactPath = () => `/contact`;
export const tokushohoPath = () => `/tokushoho`;
export const accountSettingPath = () => '/account/settings';
export const accountSettingLoginPath = () => `/account/settings/login`;
export const accountSettingProfilePath = () => `/account/settings/profile`;
export const calendarIntegrationsPath = (props?: {
  from?: string;
  resources?: number;
}) => {
  let queryString = '';
  if (props) {
    queryString = toQueryString(props);
    if (queryString) queryString = `?${queryString}`;
  }
  return `/dashboard/calendars/integrations${queryString}`;
};
export const calendarIntegrationsNewPath = () =>
  `/dashboard/calendars/integrations/new`;
export const dashboardCalendarSettingPath = (props?: { from?: string }) => {
  let queryString = '';
  if (props) {
    queryString = toQueryString(props);
    if (queryString) queryString = `?${queryString}`;
  }
  return `/dashboard/calendars/settings${queryString}`;
};
export const resourceIntegrationsPath = () => {
  return `/dashboard/resources/integrations`;
};
export const accountBillingListPath = () => `/account/billings`;
export const accountBillingPath = (props: AccountBillingPageProps) =>
  `/account/billings/${props.billingId}`;
export const appCalendarPath = (
  props: AppCalendarPageProps | null = null,
  locale?: string,
) => {
  let queryString = '';
  if (props) {
    queryString = toQueryString(props);
    if (queryString) queryString = `?${queryString}`;
  }
  return `${locale ? `/${locale}` : ''}/apps/calendar${queryString}`;
};
export const appCalendarTablePath = (locale?: string) => {
  return `${locale ? `/${locale}` : ''}/apps/calendar/table`;
};
export const appCalendarYearPath = (year: number, locale?: string) => {
  return `${locale ? `/${locale}` : ''}/apps/calendar/${year}`;
};
export const appCalendarFilterPath = (filter: string, locale?: string) => {
  return `${locale ? `/${locale}` : ''}/apps/calendar/s/${filter}`;
};
export const appCalendarYearFilterPath = (
  year: number,
  filter: string,
  locale?: string,
) => {
  return `${locale ? `/${locale}` : ''}/apps/calendar/s/${filter}/${year}`;
};
export const appCalendarMonthPath = (
  year: number,
  month: number,
  locale?: string,
) => {
  return `${locale ? `/${locale}` : ''}/apps/calendar/${year}/${month}`;
};
export const appCalendarMonthFilterPath = (
  year: number,
  month: number,
  filter: string,
  locale?: string,
) => {
  return `${
    locale ? `/${locale}` : ''
  }/apps/calendar/s/${filter}/${year}/${month}`;
};

// export const helpPath = (path?: string) => {
// const host = 'https://help.jicoo.com';
// if (!path) return host;
// return `${host}/ja/${path}`;
// };

export const macAppStorePath = (locale?: string) => {
  if (locale === 'ja') {
    return `https://apps.apple.com/jp/app/id1534672999`;
  }
  return `https://apps.apple.com/us/app/id1534672999`;
};
export const iosAppStorePath = (locale?: string) => {
  if (locale === 'ja') {
    return `https://apps.apple.com/jp/app/id1534672999`;
  }
  return `https://apps.apple.com/us/app/id1534672999`;
};
export const playStorePath = () =>
  `https://play.google.com/store/apps/details?id=com.jicoo`;
export const featureListPath = (section?: string) => {
  return `/features${section || ''}`;
};
export const helpCenterPath = (path?: string, locale?: string) => {
  const rootPath = `https://help.jicoo.com`;
  if (locale && locale !== 'ja') return `${rootPath}/${locale}${path ?? ''}`;
  return `${rootPath}${path ?? ''}`;
};
export const helpCollectionPath = (id: string) => {
  return helpCenterPath(`/collections/${id}`);
};
export const helpSearchPath = (query: string) => {
  return helpCenterPath(`/search?query=${query}`);
};
export const helpPath = (id: string, locale?: string) => {
  return helpCenterPath(`/articles/${id}`, locale);
};
export const stripePath = () => 'https://stripe.com';
export const productSchedulingPath = () => {
  return `/products/scheduling`;
};
export const productSecurityPath = () => {
  return `/products/security`;
};
export const productFormPath = () => {
  return `/products/form`;
};
export const productEmbedPath = () => {
  return `/products/embed`;
};
export const productCalendarPath = () => {
  return `/products/calendar`;
};
export const productMeetingPath = () => {
  return `/products/meeting`;
};
export const productMessagingPath = () => {
  return `/products/messaging`;
};
export const productBookingPath = (fragment?: string) => {
  return `/products/booking${fragment || ''}`;
};
export const downloadsPath = () => '/downloads';
export const aboutPath = () => '/about';
export const featurePath = (slug: string) => {
  return `/features/${slug}`;
};
export const blogPath = (category: string, slug: string) => {
  switch (category) {
    case 'engineering':
      return engineeringPath(slug);
    default:
      break;
  }
  return newsPath(slug);
};
export const customerStoryListPath = () => `/customers`;
export const customerStoryPath = (slug: string) => {
  return `/customers/${slug}`;
};
export const newsPath = (slug: string) => {
  return `/news/${slug}`;
};
export const bestPracticesPath = (slug: string) => {
  return `/best_practices/${slug}`;
};
export const careerListPath = () => {
  return `/careers`;
};
export const recruitmentPrivacyPolicyPath = () => {
  return `/privacy_policy/recruitment`;
};
export const careerPath = (slug: string) => {
  return `/careers/${slug}`;
};
export const corporateHomePath = () => `${aboutPath()}#corporate`;
export const careersHomePath = () => `${aboutPath()}#careers`;
export const engineeringHomePath = () => `${aboutPath()}#engineering`;
export const engineeringListPath = (props?: NewsListPageProps) => {
  const queryString = toQueryString(props);
  return queryString ? `/engineering?${queryString}` : `/engineering`;
};
export const engineeringPath = (slug: string) => {
  return `/engineering/${slug}`;
};
export const productIntegrationsPath = () => {
  return `/products/integrations`;
};
export const productIntegrationPath = (id: IntegrationType) => {
  return `/products/integrations/${id}`;
};
export const eventPath = (uid: string) => {
  return `/events/${uid}`;
};
export const dashboardHostEventPath = (
  uid: string,
  props?: DashboardEventPageProps,
) => {
  return props === undefined
    ? `/dashboard/host/events/${uid}`
    : `/dashboard/host/events/${uid}?${toQueryString({
        view: props.view,
        from:
          typeof props.from === 'string'
            ? encodeURIComponent(props.from)
            : undefined,
        userId: props.userId || null,
        startedAt: props.startedAt,
        endedAt: props.endedAt,
      })}`;
};

export const dashboardHostIntegrationEventPath = (
  props: {
    calendarId: string;
    uid: string;
    userId: string;
    startedAt: string;
    endedAt: string;
  },
  from?: string,
) => {
  const f = from || dashboardPath();
  const query = toQueryString({
    ...props,
    from: encodeURIComponent(f),
  });
  return `/dashboard/host/integration_events?${query}`;
};
export const dashboardGuestEventPath = (
  uid: string,
  props?: DashboardEventPageProps,
) => {
  return props === undefined
    ? `/dashboard/guest/events/${uid}`
    : `/dashboard/guest/events/${uid}?${toQueryString({
        view: props.view,
        from:
          typeof props.from === 'string'
            ? encodeURIComponent(props.from)
            : undefined,
      })}`;
};
export const dashboardHostPendingEventPath = (uid: string) => {
  return `/dashboard/host/pending_events/${uid}`;
};
export const dashboardTaskPath = (uid: string, props?: { from?: string }) => {
  return props === undefined
    ? `/dashboard/tasks/${uid}`
    : `/dashboard/tasks/${uid}?${toQueryString({
        from:
          typeof props.from === 'string'
            ? encodeURIComponent(props.from)
            : undefined,
      })}`;
};
export const dashboardFormAnswerPath = (
  formId: string,
  uid: string,
  props: { from?: string },
) => {
  return `/dashboard/forms/${formId}/answers/${uid}?${toQueryString({
    from:
      typeof props.from === 'string'
        ? encodeURIComponent(props.from)
        : undefined,
  })}`;
};
export const guestFormAnswerEditPath = (formId: string, uid: string) => {
  return `/forms/${formId}/answers/${uid}/edit`;
};
export const guestFormAnswerEditCompletePath = (
  formId: string,
  uid: string,
) => {
  return `/forms/${formId}/answers/${uid}/edit/complete`;
};
export const eventEditPath = (uid: string) => {
  return `/dashboard/host/events/${uid}/edit`;
};
export const teamPath = (teamUid: string) => {
  return `/t/${teamUid}`;
};
export const teamUsersPath = (teamUid: string) => {
  return `/t/${teamUid}/users`;
};
export const userPath = (teamUid: string, userId: string) => {
  return `/t/${teamUid}/users/${userId}`;
};

export interface EventTypePathQuery {
  guestSessionId?: string | null;
  formId?: string | null;
  formAnswerId?: string | null;
  sendUserId?: string | null;
  date?: string | null;
  time?: string | null;
  timeZone?: string | null;
  viewType?: CalendarViewType | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_source?: string | null;
  utm_content?: string | null;
  utm_term?: string | null;
}

export const eventTypePath = (
  pathParams: {
    teamSlug: string;
    eventTypeSlug: string;
    onetimeLinkId?: string;
  },
  query?: EventTypePathQuery,
) => {
  const { teamSlug, eventTypeSlug, onetimeLinkId } = pathParams;
  const pathname = onetimeLinkId
    ? `/t/${teamSlug}/e/${eventTypeSlug}/o/${onetimeLinkId}`
    : `/t/${teamSlug}/e/${eventTypeSlug}`;
  return query === undefined
    ? pathname
    : `${pathname}?${toQueryString({
        guestSessionId:
          typeof query.guestSessionId === 'string'
            ? query.guestSessionId
            : undefined,
        formId: query.formId,
        formAnswerId: query.formAnswerId,
        date: query.date,
        time: query.time,
        viewType: query.viewType,
        tz: query.timeZone,
        sendUserId: query.sendUserId,
        utm_medium: query.utm_medium,
        utm_campaign: query.utm_campaign,
        utm_source: query.utm_source,
        utm_content: query.utm_content,
        utm_term: query.utm_term,
      })}`;
};
export const eventTypeAccountLinkPath = (
  teamUid: string,
  eventTypeUid: string,
) => {
  return `/v1/teams/${teamUid}/event_types/${eventTypeUid}/validate`;
};

export const formPath = (teamSlug: string, formId: string) => {
  return `/t/${teamSlug}/f/${formId}`;
};

export const formWidgetPath = (formId: string) => {
  return `/forms/${formId}/widget`;
};
export const eventTypeWidgetPath = (eventTypeId: string) => {
  return `/event_types/${eventTypeId}/widget`;
};
export const eventTypeWidgetCompletePath = (
  eventTypeId: string,
  eventId: string,
  isCheckout: boolean,
) => {
  if (isCheckout) {
    return `/event_types/${eventTypeId}/widget/complete?eventId=${eventId}&eventTypeId=${eventTypeId}&session_id={CHECKOUT_SESSION_ID}`;
  } else {
    return `/event_types/${eventTypeId}/widget/complete?eventId=${eventId}`;
  }
};

export const eventTypeCancelPolicyPath = (
  teamUid: string,
  eventTypeSlug: string,
) => {
  return `/t/${teamUid}/e/${eventTypeSlug}/cancel_policy`;
};
export const eventTypeTermPath = (teamUid: string, eventTypeSlug: string) => {
  return `/t/${teamUid}/e/${eventTypeSlug}/terms`;
};
export const eventTypeTokushohoPath = (
  teamUid: string,
  eventTypeSlug: string,
) => {
  return `/t/${teamUid}/e/${eventTypeSlug}/tokushoho`;
};
export const pendingEventCancellationPath = (
  pendingEventId: string,
  pendingEventUserUid: string,
) =>
  `/pending_events/${pendingEventId}/cancellation?uid=${pendingEventUserUid}`;
export const pendingEventCompletePath = (
  pendingEventUid: string,
  pendingEventUserId: string,
) => {
  return `/pending_events/${pendingEventUid}/complete?uid=${pendingEventUserId}`;
};
export const eventTypeCompletePath = (
  eventTypeId: string,
  eventId: string,
  isCheckout: boolean,
) => {
  if (isCheckout) {
    return `/event_types/${eventTypeId}/complete?eventId=${eventId}&eventTypeId=${eventTypeId}&session_id={CHECKOUT_SESSION_ID}`;
  } else {
    return `/event_types/${eventTypeId}/complete?eventId=${eventId}`;
  }
};
export const requestPaymentCompletePath = (
  eventTypeId: string,
  eventId: string,
) => {
  return `/event_types/${eventTypeId}/payment/complete?eventId=${eventId}&eventTypeId=${eventTypeId}&session_id={CHECKOUT_SESSION_ID}`;
};
export const authVerifySentPath = (props?: { mobileAppUser?: number }) => {
  const query = props
    ? toQueryString({
        mobileAppUser: props.mobileAppUser,
      })
    : null;
  return !query ? `/auth/verify_sent` : `/auth/verify_sent?${query}`;
};

export const dashboardPath = (props?: {
  current?: string;
  openSupport?: 0 | 1;
  user?: string | null;
  project?: string | null;
  source?: string;
  medium?: string;
}) => {
  const query = props
    ? toQueryString({
        current: props.current,
        openSupport: props.openSupport,
        user: props.user,
        project: props.project,
        utm_source: props.source,
        utm_medium: props.medium,
      })
    : null;
  return !query ? `/dashboard` : `/dashboard?${query}`;
};

export const dashboardStatsPath = (props?: {
  date?: string;
  userId?: string | null;
  projectId?: string | null;
}) => {
  const query = props
    ? toQueryString({
        date: props.date,
        user: props.userId,
        project: props.projectId,
      })
    : null;
  return !query ? `/dashboard/analytics` : `/dashboard/analytics?${query}`;
};
export const dashboardStatsFormPath = (props?: {
  date?: string;
  userId?: string | null;
  projectId?: string | null;
}) => {
  const query = props
    ? toQueryString({
        date: props.date,
        user: props.userId,
        project: props.projectId,
      })
    : null;
  return !query
    ? `/dashboard/analytics/forms`
    : `/dashboard/analytics/forms?${query}`;
};
export const dashboardStatsManagementPath = (props?: {
  date?: string;
  userId?: string | null;
  projectId?: string | null;
}) => {
  const query = props
    ? toQueryString({
        date: props.date,
        user: props.userId,
        project: props.projectId,
      })
    : null;
  return !query
    ? `/dashboard/analytics/managements`
    : `/dashboard/analytics/managements?${query}`;
};

export const onboardCompletePath = () => {
  // `/onboard/complete`;
  return `/dashboard?onboard=1`;
};
export const reactivateCompletePath = () => {
  return `/dashboard?reactivation=1`;
};
export const onboardPath = (props?: { step?: number; to?: string }) =>
  props === undefined ? `/onboard` : `/onboard?${toQueryString(props)}`;

export const integrationListPath = () => `/dashboard/integrations`;
export const integrationUserListPath = () => `/dashboard/integrations/users`;
export const integrationPath = (
  integrationId: number | string,
  props?: { from?: string },
) => {
  return `/dashboard/integrations/${integrationId}`;
};

export const adminSettingPath = () => `/admin/settings`;
export const adminUserListPath = () => `/admin/users`;
export const adminSecurityPath = () => `/admin/security`;
export const adminActivityLogPath = () => `/admin/activity_logs`;
export const adminDataManagementPath = () => `/admin/data_management`;
export const adminPaymentPath = (props?: {
  scheduleDowngarde?: number;
  cancelDowngarde?: number;
}) =>
  props === undefined
    ? `/admin/payment`
    : `/admin/payment?${toQueryString(props)}`;
export const servicePaymentPath = () => `/dashboard/services/payment`;
export const serviceBusinessProfilePath = (props?: { onboard?: number }) =>
  props === undefined
    ? `/dashboard/services/business_profile`
    : `/dashboard/services/business_profile?${toQueryString(props)}`;
export const stripeConnectDashboardPath = (stripeAccountId: string) =>
  `https://dashboard.stripe.com/${stripeAccountId}`;
export const stripeConnectPaymentIntentPath = (stripePaymentIntentId: string) =>
  `https://dashboard.stripe.com/payments/${stripePaymentIntentId}`;
export const stripeDashboardSubscriptionPath = (subscriptionId: string) =>
  `https://dashboard.stripe.com/subscriptions/${subscriptionId}`;
export const stripeDashboardCustomerPath = (customerId: string) =>
  `https://dashboard.stripe.com/customers/${customerId}`;

export const adminSubscriptionPlanUpdatedPath = (
  props: AdminPlanUpdatedPageProps,
  isCheckout: boolean,
) =>
  `/admin/subscriptions/plan_updated?${
    isCheckout ? 'session={CHECKOUT_SESSION_ID}&' : ''
  }plan=${props.planId}&quantity=${props.quantity}&interval=${
    props.intervalType
  }`;

export const pricingPath = (props: PricingPageProps | null = null) => {
  const url = '/pricing';
  if (!props) return url;

  const params = new URLSearchParams();
  if (props.intervalType) {
    params.set('interval', String(props.intervalType));
  }
  if (props.cancelPlanId) {
    params.set('cancel', String(props.cancelPlanId));
  }
  return `${url}?${params.toString()}`;
};

export interface ResourceListPageProps {
  page?: number;
}
export interface NewsListPageProps {
  page?: number;
}
export const newsListPath = (props?: NewsListPageProps) => {
  const queryString = toQueryString(props);
  return queryString ? `/news?${queryString}` : `/news`;
};
export const bestPracticesListPath = (props?: NewsListPageProps) => {
  const queryString = toQueryString(props);
  return queryString ? `/best_practices?${queryString}` : `/best_practices`;
};

export const changelogListPath = (props?: { page?: number }) => {
  let suffix = '';
  if (props) {
    const params = new URLSearchParams();
    if (props.page) {
      params.set('page', String(props.page));
    }
    suffix = `?${params.toString()}`;
  }
  return `/changelog${suffix}`;
};
export const changelogPath = (slug: string) => {
  return `/changelog/${slug}`;
};

export interface ResourceCategoryPageProps {
  category: string;
  tag?: string | null;
  page?: number;
}
export interface ResourceTagPageProps {
  tag: string;
  category?: string | null;
  page?: number;
}

export const resourceListPath = (props?: ResourceListPageProps) => {
  const queryString = toQueryString(props);
  return queryString ? `/magazine?${queryString}` : `/magazine`;
};

export const resourceCategoryPath = (props: ResourceCategoryPageProps) => {
  let path = `/magazine/${props.category}`;
  if (props.tag) {
    path = `/magazine/${props.category}/tags/${props.tag}`;
  }
  if (props.page) {
    path = `${path}?page=${props.page}`;
  }
  return path;
};

export const resourceTagListPath = () => {
  return '/magazine/tags';
};
export const resourceTagPath = (props: ResourceTagPageProps) => {
  let path = `/magazine/tags/${props.tag}`;
  if (props.page) {
    path = `${path}?page=${props.page}`;
  }
  return path;
};

export const resourcePath = (category: string, slug: string) =>
  `/magazine/${category}/${encodeURIComponent(slug)}`;

export const serviceListPath = () => {
  return `/dashboard/services`;
};
export const serviceTermListPath = () => {
  return `/dashboard/terms`;
};
export const serviceTaxRateListPath = () => {
  return `/dashboard/services/tax_rates`;
};
export const hostTaxRateNewPath = () => `/dashboard/services/tax_rates/new`;
export const hostTaxRateEditPath = (props: HostTaxRateEditPageProps) =>
  `/dashboard/services/tax_rates/${props.taxRateId}/edit`;

export const cancelPolicyListPath = () => {
  return `/dashboard/terms`;
};
export const hostCancelPolicyNewPath = (
  props?: HostCancelPolicyNewPageProps,
) => {
  const params = new URLSearchParams();
  if (props?.from) params.set('from', props.from);
  const q = params.toString();
  return `/dashboard/cancel_policies/new${q && `?${q}`}`;
};
export const hostCancelPolicyEditPath = (
  props: HostCancelPolicyEditPageProps,
) => {
  const params = new URLSearchParams();
  if (props.from) params.set('from', props.from);
  const q = params.toString();
  return `/dashboard/cancel_policies/${props.cancelPolicyId}/edit${
    q && `?${q}`
  }`;
};

export const termListPath = () => {
  return `/dashboard/terms`;
};
export const hostTermNewPath = (props?: HostTermNewPageProps) => {
  const params = new URLSearchParams();
  if (props?.from) params.set('from', props.from);
  const q = params.toString();
  return `/dashboard/terms/new${q && `?${q}`}`;
};
export const hostTermEditPath = (props: HostTermEditPageProps) => {
  const params = new URLSearchParams();
  if (props.from) params.set('from', props.from);
  const q = params.toString();
  return `/dashboard/terms/${props.termId}/edit${q && `?${q}`}`;
};

export const teamOgpImagePath = (uid: string) => {
  return cdnApiAssetPath(`/images/cover/teams/${uid}`);
};
export const userOgpImagePath = (teamUid: string, uid: string) => {
  return cdnApiAssetPath(`/images/cover/teams/${teamUid}/users/${uid}`);
};
export const eventTypeOgpImagePath = (uid: string) => {
  return cdnApiAssetPath(`/images/cover/event_types/${uid}`);
};

export const formOgpImagePath = (uid: string) => {
  return cdnApiAssetPath(`/images/cover/forms/${uid}`);
};

export const eventOgpImagePath = (uid: string) => {
  return cdnApiAssetPath(`/images/cover/events/${uid}`);
};

export const resourceOgpImagePath = (
  slug: string,
  timestamp: number | null,
) => {
  return cdnApiAssetPath(`/images/cover/magazine/${slug}/${timestamp}`);
};

export const notificationsPath = () => {
  return `/account/notifications`;
};
export const messagesPath = () => {
  return `/account/messages`;
};

export const corporateContactPath = (locale?: string) => {
  if (locale === 'ja') {
    return formPath('jicoo', 'Ut9E3nh5_6Dm');
  }
  return formPath('jicoo', '_J3EpBefU369');
};

export const feeHelpPath = helpPath('7155245');
export const stripeFeePath = (locale?: string) => {
  if (locale === 'ja') {
    return 'https://stripe.com/jp/pricing';
  }
  return 'https://stripe.com/pricing';
};

export const formCompletePath = (
  teamId: string,
  formId: string,
  formAnswerId: string,
) => {
  return `/t/${teamId}/f/${formId}/answers/${formAnswerId}/complete`;
};
export const formWidgetCompletePath = (
  formId: string,
  formAnswerId: string,
) => {
  return `/forms/${formId}/answers/${formAnswerId}/widget/complete`;
};
export const dashboardFormPath = (props?: {
  formId: string;
  filters?: {
    sort?: string;
    order?: string;
  };
}) => {
  if (!props) return dashboardFormListPath();
  const { formId, filters } = props;
  const params = new URLSearchParams();
  if (filters?.sort) {
    params.set('sort', filters.sort);
  }
  if (filters?.order) {
    params.set('order', filters.order);
  }
  const query = params.toString();
  const url = `/dashboard/forms/${formId}`;
  return query ? `${url}?${query}` : url;
};

export const dashboardFormAnswerListExportPath = (props?: {
  teamId: string;
  formId: string;
  timeZone: string;
  filters?: {
    sort?: string;
    order?: string;
  };
}) => {
  if (!props) return dashboardFormListPath();
  const { teamId, formId, filters } = props;
  const params = new URLSearchParams();
  params.set('timeZone', props.timeZone);
  if (filters?.sort) {
    params.set('sort', filters.sort);
  }
  if (filters?.order) {
    params.set('order', filters.order);
  }
  const query = params.toString();
  const url = apiPath(`/v1/teams/${teamId}/forms/${formId}/answers/export_csv`);
  return query ? `${url}?${query}` : url;
};

export const teamNewPath = (props: { from: string }) => {
  const params = new URLSearchParams();
  params.set('from', props.from);
  const query = params.toString();
  return `/admin/teams/new?${query}`;
};

export const integrationEventTypeSettingPath = (
  integrationId: IntegrationType,
  eventTypeId: string,
  props?: { from?: string },
) => {
  const url = `/dashboard/integrations/${integrationId}/settings`;
  const params = new URLSearchParams();
  params.set('eventTypeId', eventTypeId);
  if (props && props.from) {
    params.set('from', props.from);
  }
  return `${url}?${params.toString()}`;
};

export const integrationFormSettingPath = (
  integrationId: IntegrationType,
  formId: string | number,
  props?: { from?: string },
) => {
  const url = `/dashboard/integrations/${integrationId}/settings`;
  const params = new URLSearchParams();
  params.set('formId', formId.toString());
  if (props && props.from) {
    params.set('from', props.from);
  }
  return `${url}?${params.toString()}`;
};

export const integrationSettingPath = (
  integrationId: IntegrationType | string,
) => {
  return `/dashboard/integrations/${integrationId}/settings`;
};

export const resourceSetNewPath = (eventTypeUid: string) => {
  return `/dashboard/event_types/${eventTypeUid}/resource_sets/new`;
};

export const resourceSetEditPath = (eventTypeUid: string, id: number) => {
  return `/dashboard/event_types/${eventTypeUid}/resource_sets/${id}/edit`;
};

export const googleCalendarBrowserResourcesPath = () => {
  return 'https://calendar.google.com/calendar/u/0/r/settings/browseresources';
};

export const pricingStartupWidgetPath = (locale: string) => {
  if (locale === 'ja') {
    return 'https://www.jicoo.com/forms/fOKo0bbm83jr/widget';
  }
  return 'https://www.jicoo.com/forms/L5bMd87yCvXh/widget';
};
export const pricingEducationWidgetPath = (locale: string) => {
  if (locale === 'ja') {
    return 'https://www.jicoo.com/forms/sz1-A3GgFReu/widget';
  }
  return 'https://www.jicoo.com/forms/fAGDAVnA6ItD/widget';
};

export const dashboardNewPath = (from?: string) => {
  const params = new URLSearchParams();
  params.set('from', from || dashboardPath());
  return `/dashboard/new?${params.toString()}`;
};
